import { useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowRightCircle, ChevronDown, ChevronRight, ChevronUp } from 'lucide-react'

import { MobileAppLayout } from '@/components/MobileAppLayout'
import { StatusHistoryPill } from '@/components/StatusHistoryPill'
import { type CalloutStatus } from '@/db/domain/calloutStatus'
import type { SessionSubtype, StatusChangeLog } from '@/lib/schemas/db'
import { cn } from '@/lib/utils'
import { toZaDateKey, toZaTime } from '@/lib/datetime'
import { useLocalStorageState } from '@/lib/localStorage'
import type { ServiceType } from '@/components/BookingCard'

export type NextCalloutsMobileItem = {
  /** Public-facing ID shown in UI/URLs */
  id: string
  /** Private/internal DB id for mutations */
  privateId?: string
  customerName: string
  customerId?: string
  customerPublicId?: string
  issueSummary: string
  status: CalloutStatus
  serviceType?: ServiceType
  sessionSubtype?: SessionSubtype
  contactAddress: string
  startTime: string
  endTime?: string
  isWholeDay?: boolean
  statusChanges?: StatusChangeLog[]
}

export interface NextCalloutsMobileScreenProps {
  /** All upcoming Callouts for the current technician. */
  callouts: NextCalloutsMobileItem[]
  /**
   * Local date key used to build Today/Tomorrow sections in the prototype.
   * Format: YYYY-MM-DD
   */
  todayDate: string
  /**
   * When Today+Tomorrow have fewer than this count, "Up next" appears
   * and shows the next `upNextCount` Callouts.
   */
  upNextThreshold?: number
  upNextCount?: number
  /** Called when the user selects a Callout row. */
  onSelectCallout?: (id: string) => void
  /** Called when the user taps Log work / Resume. */
  onLogWork?: (id: string) => void
  /** Called when the user changes the Callout status via the dot menu. */
  onChangeStatus?: (id: string, nextStatus: CalloutStatus) => void
  headerRight?: ReactNode
  /** Optional key scope for persisted UI state (e.g. current user id). */
  storageScope?: string
}

function parseLocalDateKey(dateKey: string) {
  return new Date(`${dateKey}T00:00:00`)
}

function toLocalDateKey(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function addDaysLocal(dateKey: string, days: number) {
  const date = parseLocalDateKey(dateKey)
  date.setDate(date.getDate() + days)
  return toLocalDateKey(date)
}

function formatDayLabel(dateKey: string) {
  const date = parseLocalDateKey(dateKey)
  return date.toLocaleDateString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

function formatTime(isoLike: string) {
  if (!isoLike) return ''
  return toZaTime(isoLike)
}

function timeRange(callout: NextCalloutsMobileItem) {
  if (callout.isWholeDay) return 'All day'
  const start = formatTime(callout.startTime)
  const end = callout.endTime ? formatTime(callout.endTime) : ''
  return end ? `${start}–${end}` : start
}

function dayKeyFromCallout(callout: NextCalloutsMobileItem) {
  return toZaDateKey(callout.startTime)
}

function sortByStartTime(a: NextCalloutsMobileItem, b: NextCalloutsMobileItem) {
  return a.startTime.localeCompare(b.startTime)
}

function Section(props: {
  title: string
  dateKey: string
  items: NextCalloutsMobileItem[]
  getAuthorDisplayName?: (authorId: string) => string
  emptyLabel: string
  isCollapsed: boolean
  onToggle: () => void
  onSelectCallout?: (id: string) => void
  onLogWork?: (id: string) => void
  onPrepInvoice?: (id: string) => void
}) {
  const {
    title,
    dateKey,
    items,
    emptyLabel,
    isCollapsed,
    onToggle,
    onSelectCallout,
    onLogWork,
    onPrepInvoice,
    getAuthorDisplayName,
  } = props
  const count = items.length

  return (
    <section className="space-y-2">
      <button
        type="button"
        className="flex w-full items-baseline justify-between px-4 text-left"
        onClick={onToggle}
        aria-expanded={!isCollapsed}
      >
        <div className="flex items-baseline gap-2">
          <div className="flex items-center gap-2">
            {isCollapsed ? (
              <ChevronDown
                className="h-4 w-4 text-slate-400"
                aria-hidden="true"
              />
            ) : (
              <ChevronUp
                className="h-4 w-4 text-slate-400"
                aria-hidden="true"
              />
            )}
            <h2 className="text-sm font-semibold text-slate-900">
              {title}
              {isCollapsed ? ` (${count})` : ''}
            </h2>
          </div>
          <div className="text-xs text-slate-500">
            · {formatDayLabel(dateKey)}
          </div>
        </div>
        <div className="text-xs text-slate-500">{count}</div>
      </button>

      {!isCollapsed ? (
        <div className="space-y-2 px-4">
          {items.map((callout) => (
            <CalloutRow
              key={callout.id}
              callout={callout}
              onSelectCallout={onSelectCallout}
              onLogWork={onLogWork}
              onPrepInvoice={onPrepInvoice}
              getAuthorDisplayName={getAuthorDisplayName}
            />
          ))}

          {items.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-white px-3 py-3 text-sm text-slate-600">
              {emptyLabel}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

function CalloutRow(props: {
  callout: NextCalloutsMobileItem
  onSelectCallout?: (id: string) => void
  onLogWork?: (id: string) => void
  onPrepInvoice?: (id: string) => void
  getAuthorDisplayName?: (authorId: string) => string
  showDate?: boolean
}) {
  const {
    callout,
    onSelectCallout,
    onLogWork,
    onPrepInvoice,
    getAuthorDisplayName,
    showDate = false,
  } = props

  const serviceType = callout.serviceType ?? 'Callout'
  const sessionSubtypeLabel = callout.sessionSubtype ?? 'TeamViewer Session'
  const serviceTypeLabel = serviceType === 'Session' ? sessionSubtypeLabel : serviceType

  const isInvoiced = callout.status === 'Invoiced'
  const shouldOpenLogWork =
    callout.status === 'Arrived' ||
    callout.status === 'LeftSite' ||
    callout.status === 'Started' ||
    callout.status === 'Ended'
  const dateKey = dayKeyFromCallout(callout)
  const scheduleLabel = showDate
    ? `${formatDayLabel(dateKey)} · ${timeRange(callout)}`
    : timeRange(callout)

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex w-full items-start justify-between gap-3 px-3 py-3 text-left">
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              {callout.customerId ? (
                <Link
                  to="/customers/$id"
                  params={{ id: callout.customerPublicId ?? callout.customerId }}
                  className="truncate text-sm font-semibold text-blue-700 hover:underline"
                >
                  {callout.customerName}
                </Link>
              ) : (
                <div className="truncate text-sm font-semibold text-slate-900">
                  {callout.customerName}
                </div>
              )}
              <div className="mt-0.5 truncate text-xs text-slate-500">
                {callout.contactAddress}
              </div>
            </div>
            <ChevronRight
              className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400"
              aria-hidden="true"
            />
          </div>

          <div className="mt-2 inline-flex items-center gap-1 text-sm text-slate-600">
            <span>
              <span className="font-medium">Issue:</span> {callout.issueSummary}
            </span>
            <Link
              to={serviceType === 'Session' ? '/sessions/$id' : '/callouts/$id'}
              params={{ id: callout.id }}
              className="text-blue-700 hover:text-blue-800"
              aria-label="Open service details"
              title="Open service details"
            >
              <ArrowRightCircle className="h-3.5 w-3.5" />
            </Link>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span
              className={
                isInvoiced
                  ? 'rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700'
                  : serviceType === 'Session'
                    ? sessionSubtypeLabel === 'Sit-down Session'
                      ? 'rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700'
                      : 'rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700'
                    : 'rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-[11px] font-medium text-purple-700'
              }
            >
              {serviceTypeLabel}
            </span>
            <span className="text-xs font-medium text-slate-700">
              {scheduleLabel}
            </span>
            <StatusHistoryPill
              currentStatus={callout.status}
              statusChanges={callout.statusChanges}
              getAuthorDisplayName={getAuthorDisplayName}
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-3 py-2">
        <button
          type="button"
          className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800"
          onClick={() => {
            if (isInvoiced) {
              onPrepInvoice?.(callout.id)
              return
            }
            if (shouldOpenLogWork) {
              onLogWork?.(callout.id)
              return
            }
            onSelectCallout?.(callout.id)
          }}
        >
          Open
        </button>
      </div>
    </div>
  )
}

export interface NextCalloutsListProps {
  callouts: NextCalloutsMobileItem[]
  todayDate: string
  getAuthorDisplayName?: (authorId: string) => string
  upNextThreshold?: number
  upNextCount?: number
  onSelectCallout?: (id: string) => void
  onLogWork?: (id: string) => void
  onPrepInvoice?: (id: string) => void
  className?: string
  /** Optional key scope for persisted UI state (e.g. current user id). */
  storageScope?: string
}

export function NextCalloutsList(props: NextCalloutsListProps) {
  const {
    callouts,
    todayDate,
    getAuthorDisplayName,
    upNextCount = 7,
    onLogWork,
    onPrepInvoice,
    className,
    storageScope,
  } = props

  const [isUpcomingExpanded, setIsUpcomingExpanded] = useLocalStorageState(
    'next-callouts.upcoming-expanded',
    false,
    { scope: storageScope },
  )
  const [upcomingVisibleCount, setUpcomingVisibleCount] = useState(upNextCount)
  const [isTodayCollapsed, setIsTodayCollapsed] = useLocalStorageState(
    'next-callouts.today-collapsed',
    false,
    { scope: storageScope },
  )
  const [isTomorrowCollapsed, setIsTomorrowCollapsed] = useLocalStorageState(
    'next-callouts.tomorrow-collapsed',
    false,
    { scope: storageScope },
  )

  const tomorrowDate = addDaysLocal(todayDate, 1)

  const upcoming = [...callouts].sort(sortByStartTime)
  const today = upcoming.filter((c) => dayKeyFromCallout(c) === todayDate)
  const tomorrow = upcoming.filter((c) => dayKeyFromCallout(c) === tomorrowDate)

  const shown = new Set<string>([...today, ...tomorrow].map((c) => c.id))
  const upcomingBeyondTomorrow = upcoming.filter(
    (callout) =>
      !shown.has(callout.id) &&
      parseLocalDateKey(dayKeyFromCallout(callout)) >
        parseLocalDateKey(tomorrowDate),
  )

  return (
    <div className={cn('space-y-4 bg-slate-50 py-4', className)}>
      <Section
        title="Today"
        dateKey={todayDate}
        items={today}
        getAuthorDisplayName={getAuthorDisplayName}
        emptyLabel="No services today."
        isCollapsed={isTodayCollapsed}
        onToggle={() => setIsTodayCollapsed((prev) => !prev)}
        onSelectCallout={props.onSelectCallout}
        onLogWork={onLogWork}
        onPrepInvoice={onPrepInvoice}
      />

      <Section
        title="Tomorrow"
        dateKey={tomorrowDate}
        items={tomorrow}
        getAuthorDisplayName={getAuthorDisplayName}
        emptyLabel="No services tomorrow."
        isCollapsed={isTomorrowCollapsed}
        onToggle={() => setIsTomorrowCollapsed((prev) => !prev)}
        onSelectCallout={props.onSelectCallout}
        onLogWork={onLogWork}
        onPrepInvoice={onPrepInvoice}
      />

      <section className="space-y-2">
        <button
          type="button"
          className="flex w-full items-baseline justify-between px-4 text-left"
          onClick={() => {
            setIsUpcomingExpanded((prev) => {
              const nextValue = !prev
              if (nextValue) {
                setUpcomingVisibleCount(upNextCount)
              }
              return nextValue
            })
          }}
          aria-expanded={isUpcomingExpanded}
        >
          <div className="flex items-baseline gap-2">
            <div className="flex items-center gap-2">
              {isUpcomingExpanded ? (
                <ChevronUp
                  className="h-4 w-4 text-slate-400"
                  aria-hidden="true"
                />
              ) : (
                <ChevronDown
                  className="h-4 w-4 text-slate-400"
                  aria-hidden="true"
                />
              )}
              <h2 className="text-sm font-semibold text-slate-900">
                Upcoming
              </h2>
            </div>
            <div className="text-xs text-slate-500">
              ·{' '}
              {upcomingBeyondTomorrow.length === 0
                ? 'No upcoming'
                : `${upcomingBeyondTomorrow.length} upcoming`}
            </div>
          </div>
        </button>

        {!isUpcomingExpanded ? null : (
          <div className="space-y-2 px-4">
            {upcomingBeyondTomorrow.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-white px-3 py-3 text-sm text-slate-600">
                No upcoming services.
              </div>
            ) : (
              <>
                {upcomingBeyondTomorrow
                  .slice(0, upcomingVisibleCount)
                  .map((callout) => (
                    <CalloutRow
                      key={callout.id}
                      callout={callout}
                      onSelectCallout={props.onSelectCallout}
                      onLogWork={onLogWork}
                      onPrepInvoice={onPrepInvoice}
                      getAuthorDisplayName={getAuthorDisplayName}
                      showDate
                    />
                  ))}
                {upcomingVisibleCount < upcomingBeyondTomorrow.length ? (
                  <button
                    type="button"
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                    onClick={() =>
                      setUpcomingVisibleCount((count) => count + upNextCount)
                    }
                  >
                    Load next {upNextCount}
                  </button>
                ) : null}
              </>
            )}
          </div>
        )}
      </section>

    </div>
  )
}

export function NextCalloutsMobileScreen(props: NextCalloutsMobileScreenProps) {
  const { headerRight, ...listProps } = props

  return (
    <MobileAppLayout
      standalone={false}
      header={(
        <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Jobcard
            </div>
            <div className="text-base font-semibold text-slate-900">
              Next callouts
            </div>
          </div>
          {headerRight ? (
            <div className="flex items-center gap-2">{headerRight}</div>
          ) : (
            <div className="h-8 w-8 rounded-full bg-slate-200" />
          )}
        </div>
      )}
    >
      <NextCalloutsList {...listProps} />
    </MobileAppLayout>
  )
}

export const demoNextCalloutsMobileToday = '2026-02-13'

export const demoNextCalloutsMobileCallouts: NextCalloutsMobileItem[] = [
  {
    id: 'callout_001',
    customerName: 'Acme Corp Facilities',
    contactAddress: '45 Industrial Way, Unit 12, Cape Town',
    issueSummary: 'No internet in reception. Suspect router power supply.',
    status: 'Booked',
    startTime: '2026-02-13T09:00:00',
    endTime: '2026-02-13T10:30:00',
  },
  {
    id: 'callout_002',
    customerName: 'John Doe',
    contactAddress: '123 Main Road, Business Park, Unit 4B',
    issueSummary: 'Investigate intermittent power trips in office block.',
    status: 'LeftSite',
    startTime: '2026-02-13T13:00:00',
    endTime: '2026-02-13T14:00:00',
  },
  {
    id: 'callout_003',
    customerName: 'Bright Lights Store',
    contactAddress: 'Mall Entrance 3, Century City',
    issueSummary: 'Replace LED driver and verify emergency lighting test.',
    status: 'Review',
    startTime: '2026-02-14T11:00:00',
    endTime: '2026-02-14T12:00:00',
  },
  {
    id: 'callout_004',
    customerName: 'Northwind Traders',
    contactAddress: 'Warehouse 7, Dockside Rd',
    issueSummary: 'After-hours callout: restore power to cold room.',
    status: 'Booked',
    startTime: '2026-02-15T18:30:00',
    endTime: '2026-02-15T20:00:00',
  },
  {
    id: 'callout_005',
    customerName: 'City Coffee Roasters',
    contactAddress: '5 Loop St, CBD',
    issueSummary: 'POS network dropping out when espresso machine runs.',
    status: 'Booked',
    startTime: '2026-02-17T08:30:00',
    endTime: '2026-02-17T09:30:00',
  },
  {
    id: 'callout_006',
    customerName: 'Sunrise Apartments',
    contactAddress: 'Block C, 88 Oak Ave',
    issueSummary: 'Gate motor keeps tripping breaker; inspect wiring.',
    status: 'New',
    startTime: '2026-02-18T10:00:00',
    endTime: '2026-02-18T11:00:00',
  },
]
